"use client";

import { formatCurrency as formatCurrencyBase } from "@/components/ui/utils";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { fetchFxRatesAction } from "../_logic/actions";
import type { FxRateMap } from "../_logic/fx-service";

/**
 * Currency rendering scope for enterprise pages. Surfaces outside the provider
 * (admin /ava, customer-portal /[event_id], public marketing) intentionally
 * fall through to native-currency rendering via `useCurrency`'s defensive default.
 */
interface CurrencyContextValue {
  targetCurrency: string;
  /** The user's saved currency; `targetCurrency` differs from it only during a view-only pick. */
  accountCurrency: string;
  rates: FxRateMap;
  setTargetCurrency: (code: string, options?: SetTargetCurrencyOptions) => Promise<void>;
}

/** Options for `setTargetCurrency`. */
export interface SetTargetCurrencyOptions {
  /** The code was just saved as the user's preference, so it also becomes `accountCurrency`. */
  isAccountCurrency?: boolean;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

interface CurrencyProviderProps {
  initialTargetCurrency: string;
  initialRates: FxRateMap;
  children: ReactNode;
}

/**
 * Wraps the enterprise app with the user's display-currency preference.
 *
 * Mounts once per request with server-fetched preference and rates; thereafter
 * the modal's setter is the only authoritative writer. A re-render with new
 * server-provided props only re-syncs state when the props differ from the last
 * setter-written value (setter wins).
 *
 * @param props.initialTargetCurrency - User's display currency from the server (`"USD"` when unset).
 * @param props.initialRates - Server-fetched FX rate map (empty only when the
 *   currency preference feature is disabled).
 * @param props.children - Subtree that should respect the preference via `useCurrency`.
 * @returns Provider element wrapping `children`.
 */
export const CurrencyProvider = ({ initialTargetCurrency, initialRates, children }: CurrencyProviderProps) => {
  // States
  const [targetCurrency, setTargetCurrencyState] = useState<string>(initialTargetCurrency);
  const [accountCurrency, setAccountCurrency] = useState<string>(initialTargetCurrency);
  const [rates, setRates] = useState<FxRateMap>(initialRates);

  // Setter-wins guards: track the last value the local setter wrote so a stale
  // prop re-render doesn't clobber it.
  const lastSetterTarget = useRef<string | null>(null);
  const lastSetterRates = useRef<FxRateMap | null>(null);

  // `ratesRef` is the up-to-date view of `rates` for the setter to read without
  // depending on `rates` in its closure. Keeps `setTargetCurrency` stable.
  const ratesRef = useRef<FxRateMap>(initialRates);
  useEffect(() => {
    ratesRef.current = rates;
  }, [rates]);

  // Handlers
  const setTargetCurrency = useCallback(async (code: string, options?: SetTargetCurrencyOptions) => {
    const normalized = code.toUpperCase();
    if (normalized !== "USD" && Object.keys(ratesRef.current).length === 0) {
      const fresh = await fetchFxRatesAction();
      ratesRef.current = fresh;
      setRates(fresh);
      lastSetterRates.current = fresh;
    }
    setTargetCurrencyState(normalized);
    lastSetterTarget.current = normalized;
    if (options?.isAccountCurrency) setAccountCurrency(normalized);
  }, []);

  // Effects
  useEffect(() => {
    setAccountCurrency(initialTargetCurrency);
  }, [initialTargetCurrency]);

  useEffect(() => {
    if (lastSetterTarget.current === initialTargetCurrency) return;
    setTargetCurrencyState((prev) => (prev === initialTargetCurrency ? prev : initialTargetCurrency));
  }, [initialTargetCurrency]);

  useEffect(() => {
    if (lastSetterRates.current === initialRates) return;
    // Server re-renders allocate a fresh rate map each time. Skip the no-op update
    // so consumers don't see context churn when nothing actually changed.
    setRates((prev) => (areRatesEqual(prev, initialRates) ? prev : initialRates));
  }, [initialRates]);

  // Render
  const value = useMemo(
    () => ({ targetCurrency, accountCurrency, rates, setTargetCurrency }),
    [targetCurrency, accountCurrency, rates, setTargetCurrency]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

/**
 * Options accepted by every formatter the hook returns.
 */
export interface FormatOptions {
  /** Drop fractional digits (forced true for zero-decimal currencies like JPY). */
  noDecimal?: boolean;
  /** Render magnitude-abbreviated (e.g. "$1.2M", "€45K") via Intl compact notation. */
  compact?: boolean;
}

/**
 * Shape returned by `useCurrency`. All formatter functions have stable identity
 * for the lifetime of `(targetCurrency, rates)` so consumers can safely place
 * them in `useMemo`/`useCallback` dependency lists.
 */
export interface UseCurrencyResult {
  targetCurrency: string;
  /** The user's saved currency, which a view-only pick returns to. */
  accountCurrency: string;
  /** Convert `amount` from `fromCurrency` to the target. Returns `null` on missing rate. */
  convert: (amount: number, fromCurrency: string) => number | null;
  /** Convert + format. Falls back to native rendering when a rate is missing. */
  format: (amount: number, fromCurrency: string, opts?: FormatOptions) => string;
  /** Format an amount already in the target currency. */
  formatTarget: (amount: number, opts?: FormatOptions) => string;
  /** Format an amount in its native currency (helper for fallback / debug). */
  formatNative: (amount: number, currency: string, opts?: FormatOptions) => string;
  /** Async setter for the user's chosen target currency. Lazy-fetches rates on demand. */
  setTargetCurrency: (code: string, options?: SetTargetCurrencyOptions) => Promise<void>;
  /** True when the active provider is in USD-only mode (no rates loaded). */
  isUsdOnly: boolean;
}

const normalizeCode = (code: string | null | undefined): string => (code || "USD").toUpperCase();

function areRatesEqual(a: FxRateMap, b: FxRateMap): boolean {
  if (a === b) return true;
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

// Module-level cache for compact `Intl.NumberFormat` instances. Construction is
// ~50-200µs per allocation; this matters for chart tick formatters that fire
// per tick × per render × per hover/resize. Cardinality is bounded by the
// handful of currencies seen in practice. The standard (non-compact) path
// delegates to `formatCurrency` in @/components/ui/utils.ts, which maintains
// its own cache.
const COMPACT_FORMATTER_CACHE = new Map<string, Intl.NumberFormat>();

function getCompactFormatter(code: string, maxFractionDigits: number): Intl.NumberFormat {
  const key = `${code}|${maxFractionDigits}`;
  const cached = COMPACT_FORMATTER_CACHE.get(key);
  if (cached) return cached;
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: code,
    notation: "compact",
    maximumFractionDigits: maxFractionDigits,
  });
  COMPACT_FORMATTER_CACHE.set(key, formatter);
  return formatter;
}

function formatWithOptions(currency: string, value: number, opts?: FormatOptions): string {
  const code = normalizeCode(currency);
  if (opts?.compact) {
    // Compact-mode max-fraction-digits is 0 when caller requests noDecimal or the
    // currency has no minor unit; otherwise default to 1 (e.g., "$1.2M").
    const maxFractionDigits = opts.noDecimal ? 0 : 1;
    return getCompactFormatter(code, maxFractionDigits).format(value);
  }
  // Non-compact path goes through the shared base formatter, which handles
  // zero-decimal-currency detection and its own Intl cache.
  return formatCurrencyBase(code, value, opts?.noDecimal);
}

const defensiveSetter = async () => {};

const buildDefensiveResult = (): UseCurrencyResult => ({
  targetCurrency: "USD",
  accountCurrency: "USD",
  convert: () => null,
  format: (amount, currency, opts) => formatWithOptions(currency, amount, opts),
  formatTarget: (amount, opts) => formatWithOptions("USD", amount, opts),
  formatNative: (amount, currency, opts) => formatWithOptions(currency, amount, opts),
  setTargetCurrency: defensiveSetter,
  isUsdOnly: true,
});

const DEFENSIVE_RESULT = buildDefensiveResult();

/**
 * Returns the active currency context, or a defensive native-rendering result
 * when called outside the provider. Safe to call from any client component —
 * surfaces outside /enterprise (admin, customer-portal) just see native currency.
 *
 * The returned object and every formatter function have stable identity over
 * `(targetCurrency, rates, setTargetCurrency)`, so consumers can safely add
 * them to `useMemo`/`useCallback` dependency arrays.
 *
 * @returns Memoized currency helpers and the current target code.
 */
export function useCurrency(): UseCurrencyResult {
  const ctx = useContext(CurrencyContext);

  return useMemo<UseCurrencyResult>(() => {
    if (!ctx) return DEFENSIVE_RESULT;

    const { targetCurrency, accountCurrency, rates, setTargetCurrency } = ctx;
    const isUsdOnly = targetCurrency === "USD" && Object.keys(rates).length === 0;

    const convert = (amount: number, fromCurrency: string): number | null => {
      const from = normalizeCode(fromCurrency);
      if (from === targetCurrency) return amount;
      const fromToUsd = from === "USD" ? 1 : rates[from];
      const targetToUsd = targetCurrency === "USD" ? 1 : rates[targetCurrency];
      if (!fromToUsd || !targetToUsd) return null;
      return (amount * fromToUsd) / targetToUsd;
    };

    const formatTarget = (amount: number, opts?: FormatOptions) => formatWithOptions(targetCurrency, amount, opts);

    const formatNative = (amount: number, currency: string, opts?: FormatOptions) =>
      formatWithOptions(currency, amount, opts);

    const format = (amount: number, fromCurrency: string, opts?: FormatOptions) => {
      const from = normalizeCode(fromCurrency);
      if (from === targetCurrency) return formatTarget(amount, opts);
      const converted = convert(amount, from);
      if (converted === null) return formatNative(amount, from, opts);
      return formatTarget(converted, opts);
    };

    return {
      targetCurrency,
      accountCurrency,
      convert,
      format,
      formatTarget,
      formatNative,
      setTargetCurrency,
      isUsdOnly,
    };
  }, [ctx]);
}

/**
 * Returns the raw context value or `null` when no provider is mounted. Used by
 * dual-mode consumers (e.g., QuotePreview) that need to branch between
 * context-driven and legacy behavior.
 *
 * @returns The active context value, or `null` when called outside the provider.
 */
export function useOptionalCurrency(): CurrencyContextValue | null {
  return useContext(CurrencyContext);
}
